import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_DRIVER_PROFILES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const MAX_AVATAR_CHARS = 900000;

/** Map Appwrite document → driver profile shape used by Fleet Maintenance. */
export const formatDriverProfile = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleId: doc.vehicleId || '',
  plate: doc.plate || '',
  driverKey: doc.driverKey || '',
  driver: doc.driver || '',
  project: doc.project || '',
  phone: doc.phone || '',
  email: doc.email || '',
  regNumber: doc.regNumber || '',
  rentalCompany: doc.rentalCompany || '',
  driverAvatar: doc.driverAvatar || '',
  driverAvatarName: doc.driverAvatarName || '',
  updatedByEmail: doc.updatedByEmail || '',
  updatedByName: doc.updatedByName || ''
});

const toPayload = (fields) => {
  const avatarRaw = String(fields.driverAvatar || '');
  const driverAvatar =
    avatarRaw.length <= MAX_AVATAR_CHARS ? avatarRaw : '';

  return {
    vehicleId: String(fields.vehicleId || '').trim(),
    plate: String(fields.plate || '')
      .trim()
      .toUpperCase()
      .replace(/\s/g, ''),
    driverKey: String(fields.driverKey || '').trim().toLowerCase(),
    driver: String(fields.driver || '').trim(),
    project: String(fields.project || '').trim(),
    phone: String(fields.phone || '').trim(),
    email: String(fields.email || '').trim().toLowerCase(),
    regNumber: String(fields.regNumber || fields.plate || '')
      .trim()
      .toUpperCase()
      .replace(/\s/g, ''),
    rentalCompany: String(fields.rentalCompany || '').trim(),
    driverAvatar,
    driverAvatarName: String(fields.driverAvatarName || '').trim(),
    updatedByEmail: String(fields.updatedByEmail || '').trim().toLowerCase(),
    updatedByName: String(fields.updatedByName || '').trim()
  };
};

export const getDriverProfileErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

/** Find profile by vehicleId, falling back to plate then email. */
export const findDriverProfile = async ({ vehicleId, plate, email } = {}) => {
  const id = String(vehicleId || '').trim();
  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');
  const emailNorm = String(email || '').trim().toLowerCase();

  const tryQuery = async (queries) => {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_DRIVER_PROFILES,
      [...queries, Query.limit(1)]
    );
    return response.documents?.[0] ? formatDriverProfile(response.documents[0]) : null;
  };

  if (id) {
    const byId = await tryQuery([Query.equal('vehicleId', id)]);
    if (byId) return byId;
  }
  if (plateNorm) {
    const byPlate = await tryQuery([Query.equal('plate', plateNorm)]);
    if (byPlate) return byPlate;
  }
  if (emailNorm) {
    return tryQuery([Query.equal('email', emailNorm)]);
  }
  return null;
};

/** Fetch profile for a vehicle (by id and/or plate). */
export const fetchDriverProfileForVehicle = async ({ vehicleId, plate, email } = {}) => {
  return findDriverProfile({ vehicleId, plate, email });
};

/** List all driver profiles (paginated). */
export const fetchAllDriverProfiles = async () => {
  const all = [];
  let cursor = null;
  const limit = 100;

  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(limit), Query.orderAsc('driver')];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_DRIVER_PROFILES,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatDriverProfile));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }

  return all;
};

/** Create or update a driver profile (including photo). */
export const upsertDriverProfile = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleId) {
    throw new Error('vehicleId is required.');
  }

  const existing = await findDriverProfile({
    vehicleId: payload.vehicleId,
    plate: payload.plate,
    email: payload.email
  });

  if (existing?.id) {
    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_DRIVER_PROFILES,
      existing.id,
      payload
    );
    return formatDriverProfile(updated);
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_DRIVER_PROFILES,
    ID.unique(),
    payload
  );
  return formatDriverProfile(created);
};
